'use client';

import { styled } from 'styled-components';

import type { SafeUrl } from '@types';
import ContentfulImage from '@dvd/components/ContentfulImage';
import { columnSpan } from '@dvd/tokens';
import { blogTokens } from '@dvd/blog/tokens';
import { bodySm } from '@dvd/blog/tokens/text-styles';

export type AuthorHeroProps = {
  /** Display name, rendered as the page <h1>. */
  name: string;
  /**
   * A Contentful image URL for the author's headshot.
   *
   * The source image should be square and 200×200px or larger; it is
   * optimized & cropped to an 84px circle via CSS.
   */
  headshot: SafeUrl;
  /**
   * Full job title, shown verbatim in the meta line — the title already
   * includes the company where applicable (e.g. "Senior Copy Editor at
   * Wistia"). Optional; the meta line omits it when absent.
   */
  jobTitle?: string;
  /**
   * Pronouns, shown in the meta line as "Pronouns: {pronouns}". Optional;
   * the meta line omits this segment when absent.
   */
  pronouns?: string;
  /** Short bio paragraph shown below the profile row. Optional; omitted when absent. */
  biography?: string;
};

const Wrapper = styled.header`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[0.75]};
  text-align: center;
  padding-inline: ${({ theme }) => theme.spacing[1]};

  ${({ theme }) => theme.mq.minWidth.large} {
    margin-block-end: ${({ theme }) => theme.spacing[2]};
    padding-inline: ${({ theme }) => theme.spacing[4]};
    gap: ${({ theme }) => theme.spacing[1.5]};
  }
`;

const Profile = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[0.75]};

  ${({ theme }) => theme.mq.minWidth.large} {
    gap: ${({ theme }) => theme.spacing[1.5]};
  }
`;

const Headshot = styled(ContentfulImage)`
  border-radius: 50%;
  flex-shrink: 0;
  height: ${({ theme }) => theme.spacing[5.25]};
  width: ${({ theme }) => theme.spacing[5.25]};
`;

const Identity = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[0.25]};

  ${({ theme }) => theme.mq.minWidth.large} {
    align-items: flex-start;
    text-align: start;
  }
`;

const Name = styled.h1`
  margin: 0;
  padding: 0;
  color: ${({ theme }) => theme.colors.blue800};
  font-size: ${blogTokens.typography.fontSize.title};

  ${({ theme }) => theme.mq.minWidth.large} {
    font-size: ${blogTokens.typography.fontSize.desktop.title};
  }
`;

const Meta = styled.p`
  ${bodySm}

  margin-block: 0;

  color: ${({ theme }) => theme.colors.blue800};
`;

const Separator = styled.span`
  margin-inline: ${({ theme }) => theme.spacing[0.5]};
`;

const Bio = styled.p`
  ${bodySm}

  margin: 0;

  /* Cap the bio to a readable prose measure so it never runs wider than the
     article grid below it — 8 of the 12 grid columns. */
  max-width: ${columnSpan(8)};
`;

/**
 * Author profile header for the `/blog/author/<slug>` page: circular
 * headshot + name, a role/pronouns line, and a short bio.
 *
 * ⚠️ Intended for use in the **page template**, not inside markdown content.
 */
const AuthorHero = ({
  name,
  headshot,
  jobTitle,
  pronouns,
  biography,
}: AuthorHeroProps) => {
  return (
    <Wrapper>
      <Profile>
        <Headshot alt={name} height={168} src={headshot} width={168} />
        <Identity>
          <Name>{name}</Name>
          {(jobTitle || pronouns) && (
            <Meta>
              {jobTitle && <span>{jobTitle}</span>}
              {jobTitle && pronouns && (
                <Separator aria-hidden="true">|</Separator>
              )}
              {pronouns && <span>{pronouns}</span>}
            </Meta>
          )}
        </Identity>
      </Profile>
      {biography && <Bio>{biography}</Bio>}
    </Wrapper>
  );
};

export default AuthorHero;
